import React, { useEffect, useState } from 'react';
import config from '../config';

interface Diagram {
  id: number;
  diagramType: string;
  diagramData?: string | null;
  imageUrl?: string | null;
  displaySize: number;
  captionPosition: number;
  defaultCaption?: string | null;
  isReusable: boolean;
  createdAt: string;
  isEnabled: boolean;
}

const getDiagramImageBaseUrl = (): string => {
  try {
    const apiUrl = new URL(config.apiBaseUrl);
    const normalizedPath = apiUrl.pathname.replace(/\/+$/, '').replace(/\/api$/i, '');
    return `${apiUrl.origin}${normalizedPath ? `${normalizedPath}/` : '/'}`;
  } catch {
    return `${config.apiBaseUrl.replace(/\/+$/, '').replace(/\/api$/i, '')}/`;
  }
};

const DIAGRAM_IMAGE_BASE_URL = getDiagramImageBaseUrl();

const normalizeImageUrlForStorage = (imageUrl: string): string => {
  const trimmedUrl = imageUrl.trim();

  if (!trimmedUrl) {
    return '';
  }

  const knownBaseUrls = [
    DIAGRAM_IMAGE_BASE_URL,
    `${config.apiBaseUrl}/`,
    config.apiBaseUrl,
  ];

  const matchedBaseUrl = knownBaseUrls.find((baseUrl) => trimmedUrl.startsWith(baseUrl));
  if (!matchedBaseUrl) {
    return trimmedUrl;
  }

  const relativePath = trimmedUrl.substring(matchedBaseUrl.length).replace(/^\/+/, '');
  return relativePath ? `/${relativePath}` : '';
};

const resolveDiagramImageUrl = (imageUrl: string): string => {
  const trimmedUrl = imageUrl.trim();

  if (trimmedUrl.startsWith('http://') || trimmedUrl.startsWith('https://')) {
    return trimmedUrl;
  }

  const normalizedPath = trimmedUrl.startsWith('/') ? trimmedUrl.substring(1) : trimmedUrl;
  return `${DIAGRAM_IMAGE_BASE_URL}${normalizedPath}`;
};

const ManageDiagrams: React.FC = () => {
  const [diagrams, setDiagrams] = useState<Diagram[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [isUploadingImage, setIsUploadingImage] = useState(false);

  const isMobile = windowWidth <= 480;
  const isTablet = windowWidth <= 768;

  const [formData, setFormData] = useState({
    id: 0,
    diagramType: '',
    diagramData: '{\n  "tikz": ""\n}',
    imageUrl: '',
    displaySize: 2,
    captionPosition: 1,
    defaultCaption: '',
    isReusable: true,
    isEnabled: true,
  });

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchDiagrams();
  }, []);

  const fetchDiagrams = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${config.apiBaseUrl}/api/diagrams`);
      if (!response.ok) throw new Error('Failed to load diagrams');
      const data = await response.json();
      setDiagrams(data);

      if (!isAdding && editingId === null && data.length > 0) {
        handleSelect(data[0]);
      }

      setError(null);
    } catch (err: any) {
      setError(err?.message || 'Failed to load diagrams');
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (diagram: Diagram) => {
    setFormData({
      id: diagram.id,
      diagramType: diagram.diagramType,
      diagramData: diagram.diagramData || '',
      imageUrl: normalizeImageUrlForStorage(diagram.imageUrl || ''),
      displaySize: diagram.displaySize ?? 2,
      captionPosition: diagram.captionPosition ?? 1,
      defaultCaption: diagram.defaultCaption || '',
      isReusable: diagram.isReusable,
      isEnabled: diagram.isEnabled,
    });
    setEditingId(diagram.id);
    setIsAdding(false);
    setIsEditMode(false);
  };

  const handleAddNew = () => {
    setFormData({
      id: 0,
      diagramType: '',
      diagramData: '{\n  "tikz": ""\n}',
      imageUrl: '',
      displaySize: 2,
      captionPosition: 1,
      defaultCaption: '',
      isReusable: true,
      isEnabled: true,
    });
    setEditingId(null);
    setIsAdding(true);
    setIsEditMode(true);
  };

  const handleEditClick = () => {
    if (editingId === null) return;
    setIsEditMode(true);
  };

  const handleCancel = () => {
    if (isAdding) {
      setIsAdding(false);
      setIsEditMode(false);

      if (diagrams.length > 0) {
        handleSelect(diagrams[0]);
      } else {
        setEditingId(null);
      }
      return;
    }

    if (editingId !== null) {
      const selectedDiagram = diagrams.find((d) => d.id === editingId);
      if (selectedDiagram) {
        handleSelect(selectedDiagram);
        return;
      }
    }

    setIsEditMode(false);
  };

  const handleSave = async () => {
    if (!formData.diagramType.trim()) {
      setError('Diagram Type is required');
      return;
    }

    if (!formData.diagramData.trim() && !formData.imageUrl.trim()) {
      setError('Provide Diagram Data, Image URL, or both');
      return;
    }

    if (formData.diagramData.trim()) {
      try {
        JSON.parse(formData.diagramData);
      } catch {
        setError('Diagram Data must be valid JSON');
        return;
      }
    }

    try {
      const normalizedImageUrl = normalizeImageUrlForStorage(formData.imageUrl);

      const payload = {
        diagramType: formData.diagramType,
        diagramData: formData.diagramData.trim() ? formData.diagramData : null,
        imageUrl: normalizedImageUrl ? normalizedImageUrl : null,
        displaySize: formData.displaySize,
        captionPosition: formData.captionPosition,
        defaultCaption: formData.defaultCaption || null,
        isReusable: formData.isReusable,
        isEnabled: formData.isEnabled,
      };

      const url = isAdding
        ? `${config.apiBaseUrl}/api/diagrams`
        : `${config.apiBaseUrl}/api/diagrams/${formData.id}`;

      const method = isAdding ? 'POST' : 'PUT';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error('Failed to save diagram');

      await fetchDiagrams();

      if (isAdding) {
        setIsAdding(false);
        setIsEditMode(false);
      } else {
        setIsEditMode(false);
      }

      setError(null);
    } catch (err) {
      setError('Failed to save diagram');
      console.error(err);
    }
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setIsUploadingImage(true);
      setError(null);

      const uploadData = new FormData();
      uploadData.append('file', file);

      const response = await fetch(`${config.apiBaseUrl}/api/diagrams/upload-image`, {
        method: 'POST',
        body: uploadData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData?.message || 'Failed to upload image');
      }

      const data = await response.json();
      setFormData((previous) => ({
        ...previous,
        imageUrl: data.imageUrl || '',
      }));
    } catch (err: any) {
      setError(err?.message || 'Failed to upload image');
    } finally {
      setIsUploadingImage(false);
      event.target.value = '';
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this diagram?')) return;

    try {
      const response = await fetch(`${config.apiBaseUrl}/api/diagrams/${id}`, {
        method: 'DELETE',
      });

      if (!response.ok) throw new Error('Failed to delete diagram');

      await fetchDiagrams();
      if (editingId === id) {
        setEditingId(null);
        setIsAdding(false);
      }
      setError(null);
    } catch (err) {
      setError('Failed to delete diagram');
      console.error(err);
    }
  };

  return (
    <div
      style={{
        maxWidth: 1400,
        margin: '0 auto',
        padding: isMobile ? '16px 8px' : isTablet ? '20px 12px' : '24px 20px',
        textAlign: 'left',
      }}
    >
      <h1
        style={{
          fontSize: isMobile ? 24 : isTablet ? 28 : 32,
          fontWeight: 700,
          marginBottom: isMobile ? 16 : 24,
          color: '#222',
        }}
      >
        Manage Diagrams
      </h1>

      {error && (
        <div
          style={{
            background: '#ffebee',
            color: '#c62828',
            padding: isMobile ? 12 : 16,
            borderRadius: 8,
            marginBottom: 16,
          }}
        >
          {error}
        </div>
      )}

      <div
        style={{
          display: 'flex',
          gap: 24,
          flexDirection: isMobile || isTablet ? 'column' : 'row',
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <button
            onClick={handleAddNew}
            style={{
              marginBottom: 24,
              padding: isMobile ? '10px 20px' : '12px 24px',
              backgroundColor: '#4caf50',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              cursor: 'pointer',
              fontSize: isMobile ? 13 : 14,
              fontWeight: 600,
            }}
          >
            ➕ Add New Diagram
          </button>

          {loading ? (
            <div style={{ textAlign: 'center', padding: 32, color: '#666' }}>Loading diagrams...</div>
          ) : diagrams.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 32, color: '#999' }}>No diagrams available</div>
          ) : (
            <div style={{ marginLeft: isMobile ? 0 : 16 }}>
              <h2 style={{ fontSize: isMobile ? 16 : 18, fontWeight: 600, marginBottom: 16, color: '#222' }}>
                {diagrams.length} Diagrams
              </h2>
              {diagrams.map((diagram) => {
                const isEditing = editingId === diagram.id;
                return (
                  <div
                    key={diagram.id}
                    onClick={() => handleSelect(diagram)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      padding: isMobile ? 12 : 16,
                      backgroundColor: isEditing ? '#e3f2fd' : '#f8fafc',
                      border: `2px solid ${isEditing ? '#2196f3' : '#94a3b8'}`,
                      borderRadius: 6,
                      marginBottom: 8,
                      flexWrap: 'wrap',
                      cursor: 'pointer',
                    }}
                  >
                    <div style={{ flex: 1, minWidth: isMobile ? '100%' : '200px' }}>
                      <p style={{ fontSize: isMobile ? 13 : 14, fontWeight: 600, margin: 0, color: '#222' }}>
                        {diagram.diagramType}
                      </p>
                      <p style={{ fontSize: isMobile ? 12 : 13, color: '#666', margin: '4px 0 0 0' }}>
                        {diagram.defaultCaption || 'No caption'}
                      </p>
                      <p style={{ fontSize: isMobile ? 11 : 12, color: '#999', margin: '4px 0 0 0' }}>
                        {diagram.isReusable ? '♻️ Reusable' : '🔒 One-time'} • {diagram.isEnabled ? '✅ Enabled' : '⛔ Disabled'}
                      </p>
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(diagram.id);
                        }}
                        style={{
                          padding: isMobile ? '6px 12px' : '8px 16px',
                          backgroundColor: '#f44336',
                          color: '#fff',
                          border: 'none',
                          borderRadius: 4,
                          cursor: 'pointer',
                          fontSize: isMobile ? 12 : 13,
                          fontWeight: 600,
                        }}
                      >
                        🗑️ Delete
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div
          style={{
            flex: 1,
            minWidth: isMobile || isTablet ? '100%' : '350px',
            maxWidth: isMobile || isTablet ? '100%' : '500px',
            backgroundColor: '#f5f5f5',
            border: '2px solid #2196f3',
            borderRadius: 8,
            padding: isMobile ? 16 : 24,
            height: 'fit-content',
            position: isMobile || isTablet ? 'relative' : 'sticky',
            top: 20,
          }}
        >
          {isAdding || editingId !== null ? (
            <>
              <h2 style={{ fontSize: isMobile ? 18 : 20, fontWeight: 600, marginBottom: 16, color: '#222' }}>
                {isAdding ? 'Add New Diagram' : isEditMode ? `Edit Diagram: ${formData.diagramType}` : `View Diagram: ${formData.diagramType}`}
              </h2>

              {!isAdding && !isEditMode && (
                <div
                  style={{
                    backgroundColor: '#ffffff',
                    border: '1px solid #dbeafe',
                    borderRadius: 6,
                    padding: 12,
                    marginBottom: 16,
                  }}
                >
                  <div style={{ marginBottom: 8, fontSize: 13, color: '#333' }}><strong>ID:</strong> {formData.id}</div>
                  <div style={{ marginBottom: 8, fontSize: 13, color: '#333' }}><strong>Caption:</strong> {formData.defaultCaption || 'No caption'}</div>
                  <div style={{ marginBottom: 8, fontSize: 13, color: '#333' }}><strong>Image URL:</strong> {formData.imageUrl || 'Not set'}</div>
                  <div style={{ marginBottom: 8, fontSize: 13, color: '#333' }}><strong>Display Size:</strong> {formData.displaySize}</div>
                  <div style={{ marginBottom: 8, fontSize: 13, color: '#333' }}><strong>Caption Position:</strong> {formData.captionPosition}</div>
                  <div style={{ marginBottom: 8, fontSize: 13, color: '#333' }}><strong>Reusable:</strong> {formData.isReusable ? 'Yes' : 'No'}</div>
                  <div style={{ marginBottom: 8, fontSize: 13, color: '#333' }}><strong>Status:</strong> {formData.isEnabled ? 'Enabled' : 'Disabled'}</div>

                  {formData.imageUrl && (
                    <div style={{ marginTop: 14 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#222', marginBottom: 8 }}>Image Preview</div>
                      <div
                        style={{
                          background: '#fff',
                          border: '1px solid #ddd',
                          borderRadius: 6,
                          padding: 10,
                        }}
                      >
                        <img
                          src={resolveDiagramImageUrl(formData.imageUrl)}
                          alt={`${formData.diagramType} diagram preview`}
                          style={{ width: '100%', height: 'auto', display: 'block', maxHeight: 260, objectFit: 'contain' }}
                        />
                      </div>
                    </div>
                  )}

                  {formData.diagramData && (
                    <div style={{ marginTop: 14 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#222', marginBottom: 8 }}>SVG Preview</div>
                      <div
                        style={{
                          background: '#fff',
                          border: '1px solid #ddd',
                          borderRadius: 6,
                          padding: 10,
                        }}
                      >
                        <img
                          src={`${config.apiBaseUrl}/api/diagrams/${formData.id}/svg`}
                          alt={`${formData.diagramType} preview`}
                          style={{ width: '100%', height: 'auto', display: 'block' }}
                        />
                      </div>
                    </div>
                  )}

                  <div style={{ marginTop: 16, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    <button
                      onClick={handleEditClick}
                      style={{
                        padding: isMobile ? '8px 16px' : '10px 20px',
                        backgroundColor: '#2196f3',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 6,
                        cursor: 'pointer',
                        fontSize: isMobile ? 13 : 14,
                        fontWeight: 600,
                      }}
                    >
                      ✎ Edit
                    </button>
                  </div>
                </div>
              )}

              {(isAdding || isEditMode) && (
                <div
                  style={{
                    backgroundColor: '#e3f2fd',
                    border: '1px solid #90caf9',
                    borderRadius: 6,
                    padding: 10,
                    marginBottom: 16,
                    fontSize: 12,
                    color: '#0d47a1',
                  }}
                >
                  {!isAdding && <div><strong>ID:</strong> {formData.id}</div>}
                  <div><strong>Mode:</strong> {isAdding ? 'Creating new diagram' : 'Editing existing diagram'}</div>
                </div>
              )}

              {(isAdding || isEditMode) && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 16, marginBottom: 16 }}>
              <div>
                <label style={{ display: 'block', fontSize: isMobile ? 12 : 13, fontWeight: 600, marginBottom: 6, color: '#222' }}>
                  Diagram Type *
                </label>
                <input
                  type="text"
                  value={formData.diagramType}
                  onChange={(e) => setFormData({ ...formData, diagramType: e.target.value })}
                  placeholder="e.g., TikZ, Geometry, Graph"
                  style={{
                    width: '100%',
                    padding: isMobile ? 10 : 12,
                    fontSize: isMobile ? 13 : 14,
                    border: '1px solid #e0e0e0',
                    borderRadius: 4,
                    boxSizing: 'border-box',
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: isMobile ? 12 : 13, fontWeight: 600, marginBottom: 6, color: '#222' }}>
                  Default Caption
                </label>
                <input
                  type="text"
                  value={formData.defaultCaption}
                  onChange={(e) => setFormData({ ...formData, defaultCaption: e.target.value })}
                  placeholder="Optional caption"
                  style={{
                    width: '100%',
                    padding: isMobile ? 10 : 12,
                    fontSize: isMobile ? 13 : 14,
                    border: '1px solid #e0e0e0',
                    borderRadius: 4,
                    boxSizing: 'border-box',
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: isMobile ? 12 : 13, fontWeight: 600, marginBottom: 6, color: '#222' }}>
                  Image URL
                </label>
                <input
                  type="text"
                  value={formData.imageUrl}
                  onChange={(e) => setFormData({ ...formData, imageUrl: e.target.value })}
                  placeholder="/uploads/diagrams/... or https://..."
                  style={{
                    width: '100%',
                    padding: isMobile ? 10 : 12,
                    fontSize: isMobile ? 13 : 14,
                    border: '1px solid #e0e0e0',
                    borderRadius: 4,
                    boxSizing: 'border-box',
                  }}
                />
                <div style={{ marginTop: 8, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                  <label
                    style={{
                      padding: '8px 14px',
                      backgroundColor: isUploadingImage ? '#94a3b8' : '#0ea5e9',
                      color: '#fff',
                      borderRadius: 6,
                      cursor: isUploadingImage ? 'not-allowed' : 'pointer',
                      fontSize: 13,
                      fontWeight: 600,
                    }}
                  >
                    {isUploadingImage ? 'Uploading...' : 'Upload Image'}
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      onChange={handleImageUpload}
                      disabled={isUploadingImage}
                      style={{ display: 'none' }}
                    />
                  </label>
                  <span style={{ fontSize: 12, color: '#666' }}>JPG, PNG, WEBP up to 2MB</span>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={{ display: 'block', fontSize: isMobile ? 12 : 13, fontWeight: 600, marginBottom: 6, color: '#222' }}>
                    Display Size
                  </label>
                  <select
                    value={formData.displaySize}
                    onChange={(e) => setFormData({ ...formData, displaySize: Number(e.target.value) })}
                    style={{
                      width: '100%',
                      padding: isMobile ? 10 : 12,
                      fontSize: isMobile ? 13 : 14,
                      border: '1px solid #e0e0e0',
                      borderRadius: 4,
                      boxSizing: 'border-box',
                    }}
                  >
                    <option value={1}>Small</option>
                    <option value={2}>Medium</option>
                    <option value={3}>Large</option>
                    <option value={4}>Full Width</option>
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: isMobile ? 12 : 13, fontWeight: 600, marginBottom: 6, color: '#222' }}>
                    Caption Position
                  </label>
                  <select
                    value={formData.captionPosition}
                    onChange={(e) => setFormData({ ...formData, captionPosition: Number(e.target.value) })}
                    style={{
                      width: '100%',
                      padding: isMobile ? 10 : 12,
                      fontSize: isMobile ? 13 : 14,
                      border: '1px solid #e0e0e0',
                      borderRadius: 4,
                      boxSizing: 'border-box',
                    }}
                  >
                    <option value={1}>Bottom</option>
                    <option value={2}>Top</option>
                    <option value={3}>Left</option>
                    <option value={4}>Right</option>
                    <option value={5}>Overlay</option>
                  </select>
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: isMobile ? 12 : 13, fontWeight: 600, marginBottom: 6, color: '#222' }}>
                  Diagram Data (JSON)
                </label>
                <textarea
                  value={formData.diagramData}
                  onChange={(e) => setFormData({ ...formData, diagramData: e.target.value })}
                  style={{
                    width: '100%',
                    minHeight: isMobile ? 180 : 240,
                    padding: isMobile ? 10 : 12,
                    fontSize: isMobile ? 12 : 13,
                    border: '1px solid #e0e0e0',
                    borderRadius: 4,
                    boxSizing: 'border-box',
                    fontFamily: 'monospace',
                    resize: 'vertical',
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: isMobile ? 13 : 14 }}>
                  <input
                    type="checkbox"
                    checked={formData.isReusable}
                    onChange={(e) => setFormData({ ...formData, isReusable: e.target.checked })}
                  />
                  Is Reusable
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: isMobile ? 13 : 14 }}>
                  <input
                    type="checkbox"
                    checked={formData.isEnabled}
                    onChange={(e) => setFormData({ ...formData, isEnabled: e.target.checked })}
                  />
                  Is Enabled
                </label>
              </div>
            </div>
              )}

              {(isAdding || isEditMode) && (
              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <button
                onClick={handleCancel}
                style={{
                  flex: 1,
                  padding: isMobile ? '10px 20px' : '12px 24px',
                  backgroundColor: '#e0e0e0',
                  color: '#333',
                  border: 'none',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontSize: isMobile ? 13 : 14,
                  fontWeight: 600,
                }}
              >
                ✕ Cancel
              </button>
              <button
                onClick={handleSave}
                style={{
                  flex: 1,
                  padding: isMobile ? '10px 20px' : '12px 24px',
                  backgroundColor: '#4caf50',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontSize: isMobile ? 13 : 14,
                  fontWeight: 600,
                }}
              >
                ✓ Save
              </button>
            </div>
              )}
            </>
          ) : (
            <div style={{ color: '#666', fontSize: 14 }}>
              Select a diagram from the left list to view details and edit.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ManageDiagrams;
